<template>
  <v-layout row justify-center>
    <dialog-wrapper :visible="dialog">
      <v-dialog v-model="dialog" max-width="800" persistent>
        <v-tooltip slot="activator" left>
          <v-btn
            slot="activator"
            fixed
            dark
            fab
            bottom
            right
            color="primary"
          >
            <v-icon>add</v-icon>
          </v-btn>
          <span v-t="'dishesContainer.orderForSomeoneElse'" />
        </v-tooltip>
        <v-card v-if="dialog" class="px-2">
          <v-card-title class="headline">
            <div style="width: 80%;">
              <span v-t="`common.forDay-${dayOfWeek}`" />
              <span v-t="`dishesContainer.modifyUserOrder`" />
            </div>
          </v-card-title>
          <v-card-title class="pt-0">
            <v-autocomplete
              v-model="selectedUser"
              :label="$t('dishesContainer.searchByName')"
              append-icon="search"
              :loading="loading"
              cache-items
              :item-text="(v) => `${v.displayName} ${v.email} (${$t('usersBalancesContainer.balance')}: ${v.balance} €)`"
              item-value="id"
              :items="items"
              return-object
              persistent-hint
              clearable
              :no-data-text="$t('dishesContainer.userNotFound')"
              :hint="`${selectedUser ? selectedUser.displayName : ''}`"
              :search-input.sync="searchQuery"
            >
              <template slot="item" slot-scope="data">
                <v-list-tile-content>
                  <v-list-tile-title>{{ data.item.displayName }}</v-list-tile-title>
                  <v-list-tile-sub-title>{{ data.item.email }}</v-list-tile-sub-title>
                </v-list-tile-content>
              </template>
            </v-autocomplete>
          </v-card-title>
          <v-card-title v-if="selectedUser" :class="$vuetify.breakpoint.smAndUp ? 'pt-0 nowrap' : 'pt-0'">
            <v-combobox
              ref="userSelectionsCombobox"
              v-model="userSelections"
              chips
              :label="$t('dishesContainer.userSelections')"
              item-text="title"
              clearable
              multiple
              append-icon=""
              readonly
              @keyup.enter="saveUserSelections"
            >
              <template slot="selection" slot-scope="data">
                <v-tooltip
                  bottom
                  :disabled="dishTitle(data.item.title) === data.item.title"
                >
                  <v-chip
                    slot="activator"
                    close
                    @input="removeFromUserSelections(data.item.id)"
                  >
                    <span>{{ dishTitle(data.item.title) }}</span>
                  </v-chip>
                  <span>{{ data.item.title }}</span>
                </v-tooltip>
              </template>
            </v-combobox>
            <div>
              <v-menu v-model="dishesListMenu" :close-on-content-click="false">
                <v-btn
                  slot="activator"
                  flat
                  color="secondary"
                  @click="focusAutocompleteField"
                  @keyup.enter="dishesListMenu=true; focusAutocompleteField()"
                >
                  <v-icon>add</v-icon>
                  <span v-t="'dishesContainer.addDish'" />
                </v-btn>
                <v-card v-if="dishesListMenu">
                  <v-autocomplete
                    ref="autocomplete"
                    class="mx-3 pr-4"
                    :label="$t('dishesContainer.dishes')"
                    item-text="title"
                    :items="mappedSelections"
                    @input="addDishSelection"
                  />
                </v-card>
              </v-menu>
            </div>
          </v-card-title>
          <v-card-title v-if="selectedUser && userSelections.length > 0" class="pt-0">
            <v-text-field
              ref="comment"
              v-model="comment"
              :label="$t('common.comment')"
              :name="$t('common.comment')"
              clearable
              :rules="[rules.lengthNotGreaterThan]"
              @keyup.enter="saveUserSelections"
            />
          </v-card-title>
          <v-card-actions>
            <v-spacer />
            <v-btn flat @click.native="closeModal"><span v-t="'common.close'" /></v-btn>
            <v-btn
              color="primary"
              :loading="saving"
              :disabled="!selectedUser || isButtonDisabled"
              @click="saveUserSelections"
            ><span v-t="'common.save'" /></v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </dialog-wrapper>
  </v-layout>
</template>

<script>
import gql from 'graphql-tag';
import ErrorHandler from '@/services/ErrorHandler';
import flattenDeep from 'lodash/flattenDeep';
import flatten from 'lodash/flatten';
import { apolloClient } from '@/apollo';
import { mapActions, mapGetters } from 'vuex';
import ValidationService from '@/services/ValidationService';
import NotificationService from '@/services/NotificationService';
import { isEqual } from 'lodash';
import DialogWrapper from '../../common/dialog-wrapper/DialogWrapper.vue';

export default {
  name: 'AddCustomOrderButton',
  components: { DialogWrapper },
  props: {
    providers: {
      type: Array,
      default: () => [],
    },
    dayOfWeek: {
      type: String,
      default: undefined,
    },
  },
  data: () => ({
    dialog: false,
    loading: false,
    saving: false,
    selectedUser: null,
    searchQuery: '',
    items: [],
    searchTimeout: null,
    userSelections: [],
    cleanUserSelections: [],
    dishesListMenu: false,
    isButtonDisabled: false,
    comment: '',
    rules: {
      lengthNotGreaterThan: ValidationService.rules.lengthNotGreaterThan('Value must not exceed 100 characters.', 100),
    },
  }),
  computed: {
    ...mapGetters('userInfoStore', ['userData']),
    mappedSelections() {
      const dishes = flattenDeep(this.providers.map(p => p.categories.map(c => c.selections.map(s => ({
        id: s.id,
        title: `${s.dishes.map(d => d.title).join(', ')} (${s.price.toFixed(2)} €)`,
        price: s.price,
        dishes: s.dishes,
      })))));

      return dishes;
    },
  },
  watch: {
    searchQuery(newValue) {
      if (newValue) {
        this.searchUsers(newValue);
      }
    },
    selectedUser(newValue) {
      if (newValue) {
        this.loadUserSelections();
      } else {
        this.items = [];
      }
    },
    dialog(newValue) {
      if (!newValue) {
        this.selectedUser = null;
      }
    },
    userSelections(newValue) {
      if (newValue.length === 0) {
        this.comment = '';
      }
    },
  },
  methods: {
    ...mapActions('dishesStore', [
      'reloadSelections',
    ]),
    focusAutocompleteField() {
      setTimeout(() => { this.$refs.autocomplete.$refs.input.click(); }, 300);
    },
    closeModal() {
      const commentChanged = this.cleanUserSelections.length > 0 && this.cleanUserSelections[0].comment !== this.comment;
      if (!isEqual(this.userSelections, this.cleanUserSelections) || commentChanged) {
        NotificationService.showUnsavedChangesModal({
          header: 'common.discardChanges',
          body: 'common.discardChangesBody',
          agreeCallback: () => this.close(),
        });
      } else {
        this.close();
      }
    },
    close() {
      this.dialog = false;
      this.userSelections = [];
      this.cleanUserSelections = [];
      this.selectedUser = null;
      this.comment = '';
    },
    addDishSelection(selection) {
      const selectedDish = this.mappedSelections.find(s => s.title === selection);
      if (this.userSelections.find(s => s.title === selectedDish.title)) {
        selectedDish.isSecondary = true;
      } else { selectedDish.isSecondary = false; }
      this.userSelections.push(selectedDish);
      this.dishesListMenu = false;
      setTimeout(() => { this.$refs.userSelectionsCombobox.focus(); }, 300);
    },
    removeFromUserSelections(id) {
      let index;
      if (this.userSelections.filter(s => s.id === id).length > 1) {
        index = this.userSelections.findIndex(s => s.id === id && s.isSecondary === true);
      } else {
        index = this.userSelections.findIndex(s => s.id === id);
      }

      this.userSelections.splice(index, 1);
    },
    dishTitle(title) {
      const maxDishChipLength = Math.round(this.$refs.userSelectionsCombobox.$el.clientWidth / 8);
      if (title.length > maxDishChipLength) {
        return `${title.substring(0, maxDishChipLength)}...`;
      }

      return title;
    },
    async searchUsers(query) {
      this.loading = true;
      clearTimeout(this.searchTimeout);
      this.searchTimeout = setTimeout(async () => {
        try {
          const { data } = await this.$apollo.query({
            fetchPolicy: 'network-only',
            query: gql`query UsersSearchQuery($query: String!, $onlyActive: Boolean) {
              usersSearch(query: $query, onlyActive: $onlyActive) {
                id
                displayName
                email
                balance
              }
            }`,
            variables: {
              query,
              onlyActive: true,
            },
          });

          this.items = data.usersSearch;
        } catch (error) {
          ErrorHandler.handleApolloError(error);
        } finally {
          this.loading = false;
        }
      }, 500);
    },
    async loadUserSelections() {
      try {
        const { data } = await this.$apollo.query({
          fetchPolicy: 'network-only',
          query: gql`query UserSelectionsQuery($dayOfWeek: DayOfWeek!, $userId: Int!) {
            userSelections(dayOfWeek: $dayOfWeek, userId: $userId) {
              id
              price
              comment
              isSecondary
              dishes: userOrderedDishes(userId: $userId) {
                id
                title
              }
            }
          }`,
          variables: {
            dayOfWeek: this.dayOfWeek,
            userId: this.selectedUser.id,
          },
        });

        if (data.userSelections) {
          this.userSelections = data.userSelections.map(s => ({
            id: s.id,
            title: `${s.dishes.map(d => d.title).join(' + ')} (${s.price.toFixed(2)} €)`,
            price: s.price,
            dishes: s.dishes,
            comment: s.comment,
            isSecondary: s.isSecondary,
          }));
        } else {
          this.userSelections = [];
        }
        this.cleanUserSelections = JSON.parse(JSON.stringify(this.userSelections));
        this.comment = this.userSelections.length > 0 ? data.userSelections[0].comment : '';
      } catch (error) {
        ErrorHandler.handleApolloError(error);
      } finally {
        this.loading = false;
      }
    },
    async saveUserSelections() {
      try {
        const commentValid = ValidationService.validate(
          Object.keys(this.$refs)
            .filter(k => k === 'comment' && this.$refs[k] !== undefined)
            .map(k => this.$refs[k]),
        );

        if (commentValid) {
          this.isButtonDisabled = true;
          this.saving = true;
          const result = await this.$apollo.mutate({
            mutation: gql`mutation SaveUserSelectionsMutation($dayOfWeek: DayOfWeek!, $requestDishIds: [RequestDishIdInput]!, $userId: Int!, $comment: String){
              saveDishes(requestDishIds: $requestDishIds, dayOfWeek: $dayOfWeek, userId: $userId, comment: $comment)
            }`,
            variables: {
              requestDishIds: flatten(this.userSelections.map(s => s.dishes.map(d => ({ dishId: d.id, isSecondary: false })))),
              dayOfWeek: this.dayOfWeek,
              userId: this.selectedUser.id,
              comment: this.comment && this.comment.trim().length > 0 ? this.comment.trim() : null,
            },
          });

          if (result) {
            if (this.selectedUser.displayName === this.userData.displayName) {
              apolloClient.queryManager.refetchQueryByName('CurrentUserQuery');
              this.reloadSelections(this.userSelections);
            }
            this.saving = false;
            this.close();
            NotificationService.showSnackbar({
              message: 'dishesContainer.successfullyOrdered',
              timeout: 5000,
              position: 'top',
            });
          }
        } else {
          setTimeout(() => {
            const invalidFieldNames = [...new Set(ValidationService.getInvalidFieldNames())];
            NotificationService.showAlert({
              message: this.$t('common.invalidFields', { fields: invalidFieldNames.join(', ') }),
              level: 'error',
            });
          });
        }
      } catch (error) {
        ErrorHandler.handleApolloError(error);
        this.saving = false;
      } finally {
        this.isButtonDisabled = false;
      }
    },
  },
};
</script>

<style scoped>
.nowrap {
  flex-flow: nowrap;
}
</style>
